import React, { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  FiBarChart2,
  FiBox,
  FiCheckSquare,
  FiClipboard,
  FiCreditCard,
  FiDollarSign,
  FiHome,
  FiImage,
  FiLogOut,
  FiPackage,
  FiUsers,
  FiMenu,
  FiX,
  FiBell,
  FiSearch,
  FiSettings,
} from "react-icons/fi";

import { logout } from "../../features/auth/authSlice";
import logo from "../../assets/melona logo.png";

const navItems = [
  { to: "/dashboard",        label: "Overview",   icon: <FiHome />, badge: null },
  { to: "/orders",           label: "Orders",     icon: <FiClipboard />, badge: null },
  { to: "/customers",        label: "Customers",  icon: <FiUsers />, badge: null },
  { to: "/payments",         label: "Payments",   icon: <FiCreditCard />, badge: null },
  { to: "/tasks",            label: "Tasks",      icon: <FiBarChart2 />, badge: "3" },
  { to: "/todo",             label: "Todo",       icon: <FiCheckSquare />, badge: null },
  { to: "/employees",        label: "Employees",  icon: <FiUsers />, badge: null },
  { to: "/dashboard/gallery",label: "Gallery",    icon: <FiImage />, badge: null },
  { to: "/stock",            label: "Stock",      icon: <FiPackage />, badge: "5" },
  { to: "/expenses",         label: "Expenses",   icon: <FiDollarSign />, badge: null },
  { to: "/expense-history",  label: "Expense History", icon: <FiDollarSign />, badge: null },
  { to: "/contact-submissions", label: "Contact Forms", icon: <FiBell />, badge: null },
  { to: "/reports",          label: "Reports",    icon: <FiBox />, badge: null },
];

const PAGE_TITLES = {
  "/dashboard":         "Dashboard Overview",
  "/orders":            "Orders Management",
  "/orders/create":     "Create New Order",
  "/customers":         "Customers",
  "/payments":          "Payments",
  "/tasks":             "Tasks",
  "/todo":              "Todo List",
  "/employees":         "Team Members",
  "/dashboard/gallery": "Gallery",
  "/stock":             "Inventory",
  "/expenses":          "Expenses",
  "/expense-history":   "Expense History",
  "/contact-submissions": "Contact Form Submissions",
  "/reports":           "Reports & Analytics",
};

const AppShell = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const initials = user?.name
    ? user.name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()
    : "MA";

  const pageTitle = PAGE_TITLES[location.pathname] || "Melona";
  const pageSubtitle = getPageSubtitle(location.pathname);

  function getPageSubtitle(path) {
    const subtitles = {
      "/dashboard": "Welcome back! Here's your business overview",
      "/orders": "Manage and track all printing orders",
      "/customers": "View and manage your customer base",
      "/tasks": "Track team tasks and assignments",
    };
    return subtitles[path] || "";
  }

  return (
    <div className="dashboard-shell-modern">
      {/* Sidebar */}
      <aside className={`dashboard-sidebar-modern ${sidebarOpen ? 'is-open' : ''}`}>
        {/* Brand */}
        <div className="sidebar-brand">
          <div className="sidebar-brand__logo">
            <img src={logo} alt="Melona" style={{ height: "32px", width: "auto" }} />
          </div>
          <button 
            className="sidebar-close"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close sidebar"
          >
            <FiX />
          </button>
        </div>

        {/* User Profile */}
        <div className="sidebar-profile">
          <div className="sidebar-profile__avatar">{initials}</div>
          <div className="sidebar-profile__info">
            <div className="sidebar-profile__name">{user?.name || "Guest"}</div>
            <div className="sidebar-profile__role">{user?.role || "viewer"}</div>
          </div>
          <button className="sidebar-profile__settings" aria-label="Settings">
            <FiSettings />
          </button>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          <div className="sidebar-nav__section">
            <span className="sidebar-nav__label">Main Menu</span>
            {navItems.slice(0, 4).map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/dashboard"}
                className={({ isActive }) =>
                  `sidebar-nav__item${isActive ? " is-active" : ""}`
                }
                onClick={() => setSidebarOpen(false)}
              >
                <span className="sidebar-nav__icon">{item.icon}</span>
                <span className="sidebar-nav__text">{item.label}</span>
                {item.badge && (
                  <span className="sidebar-nav__badge">{item.badge}</span>
                )}
              </NavLink>
            ))}
          </div>

          <div className="sidebar-nav__section">
            <span className="sidebar-nav__label">Management</span>
            {navItems.slice(4).map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `sidebar-nav__item${isActive ? " is-active" : ""}`
                }
                onClick={() => setSidebarOpen(false)}
              >
                <span className="sidebar-nav__icon">{item.icon}</span>
                <span className="sidebar-nav__text">{item.label}</span>
                {item.badge && (
                  <span className="sidebar-nav__badge">{item.badge}</span>
                )}
              </NavLink>
            ))}
          </div>
        </nav>

        {/* Logout */}
        <div className="sidebar-footer">
          <button 
            type="button" 
            className="sidebar-logout"
            onClick={() => dispatch(logout())}
          >
            <FiLogOut />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="dashboard-main-modern">
        {/* Top Bar */}
        <header className="dashboard-header-modern">
          <div className="dashboard-header__left">
            <button 
              className="dashboard-mobile-toggle"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open menu"
            >
              <FiMenu />
            </button>
            <div className="dashboard-header__title-group">
              <h1 className="dashboard-header__title">{pageTitle}</h1>
              {pageSubtitle && (
                <p className="dashboard-header__subtitle">{pageSubtitle}</p>
              )}
            </div>
          </div>

          <div className="dashboard-header__right">
            {/* Search */}
            <div className={`dashboard-search ${searchOpen ? 'is-open' : ''}`}>
              <button 
                className="dashboard-search__toggle"
                onClick={() => setSearchOpen(!searchOpen)}
                aria-label="Search"
              >
                <FiSearch />
              </button>
              <input
                type="text"
                className="dashboard-search__input"
                placeholder="Search orders, customers..."
                onBlur={() => setTimeout(() => setSearchOpen(false), 200)}
              />
            </div>

            {/* Notifications */}
            <button className="dashboard-header__action" aria-label="Notifications">
              <FiBell />
              <span className="dashboard-header__action-badge">3</span>
            </button>

            {/* User Menu */}
            <div className="dashboard-user-menu">
              <div className="dashboard-user-menu__avatar">{initials}</div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="dashboard-content">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default AppShell;
